/**
 * euConsent.ts — WEB-409 P8-F9 FE consent helpers.
 *
 * EU-membership scope mirrored from the backend arbiter
 * (backend/src/analytics/constants/eu-consent-countries.ts): EU-27 + EEA
 * (NO/IS/LI) + UK (GB) + CH. The server remains the authoritative arbiter (it
 * echoes `geo_country` from cf-ipcountry into the public DTO); this FE copy
 * only decides whether to SHOW the consent prompt. An unknown country
 * (null/undefined) is treated as consent-required (fail-closed GDPR), matching
 * BE isEuConsentRequired().
 *
 * The decision is persisted in the `w2a_consent` cookie that
 * playerPixelEvents.resolvePlayerConsent() reads (fail-closed: only 'granted'
 * enables pixel/s2s firing).
 *
 * Что добавила волна согласия 18.09 (зонт WEB-2117):
 *  - WEB-2151 п.1 — `CH` в списке стран (второй конец — серверный список,
 *    совпадение держит
 *    `backend/src/shared/guards/eu-consent-countries-ends.spec.ts`);
 *  - WEB-2145 п.2 — `clearAttributionStorage()`: отказ УБИРАЕТ уже записанные
 *    рекламные метки, иначе кнопка «Отклонить» ничего не отзывает;
 *  - WEB-2146 п.2 — `applyConsentDecision()`: согласие ДОЗАПИСЫВАЕТ метки из
 *    адресной строки (адрес переживает весь визит, поэтому у согласившихся
 *    ничего не теряется);
 *  - WEB-2140 — `resolveConsentCookieDomain()`: кука согласия на родительском
 *    домене клиента, чтобы квиз на поддомене и оплата на корневом домене не
 *    спрашивали дважды;
 *  - WEB-2162 п.4 — `resolveSharingConsent()`: сигнал браузера Global Privacy
 *    Control читается как отказ от ПЕРЕДАЧИ третьим лицам.
 */
import { PUBLIC_APEX } from '@/config/public-host';
import { isStagingConsentHost } from '@/config/publicEntryHosts';
import { captureUtmFromUrl, resetUtmCache, UTM_STORAGE_KEY } from './utmCapture';
import { captureClickIds } from './clickIdCapture';
import { forgetVisitorIdForRevocation, getVisitorId } from './visitorId';
import {
  CONSENT_COOKIE,
  readConsentDecision,
  type ConsentDecision,
} from './consentCookie';

/**
 * WEB-2146 (остаток отказа приёмки 21.09) — ЧТЕНИЕ РЕШЕНИЯ ПЕРЕЕХАЛО В ЛИСТ.
 *
 * Постоянный идентификатор посетителя (`visitorId.ts`) обязан знать решение,
 * чтобы не записывать себя на устройство европейца до «Принять». Импортировать
 * решение отсюда он не может: этот модуль сам зовёт `visitorId.ts` (очистка
 * следа при отказе), вышел бы круг модулей. Поэтому чтение живёт в
 * модуле-листе `consentCookie.ts`, а ОБА читателя берут его оттуда — второй
 * копии гейта не появилось (шрам WEB-953: своя копия расходится молча).
 *
 * Переэкспорт оставлен намеренно: прежние импортёры (`playerPixelEvents.ts`,
 * обе публичные страницы, баннер, хранилище прогресса и их тесты) продолжают
 * брать имя куки, тип решения и само чтение отсюда — правка не расползается.
 */
export { CONSENT_COOKIE, readConsentDecision };
export type { ConsentDecision };

const EU_CONSENT_COUNTRIES: ReadonlySet<string> = new Set<string>([
  // EU-27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
  // EEA (non-EU)
  'NO', 'IS', 'LI',
  // UK
  'GB',
  // WEB-2151 п.1: Швейцария живёт по собственному закону (nFADP), устроенному
  // по европейскому образцу. Добавление страны только усиливает осторожность.
  'CH',
]);

/**
 * WEB-2145 п.2 — АТРИБУЦИОННЫЕ записи, которые стираются при явном отказе.
 * Перечень — из разбора `pm_attention/2026-09-16-согласие-cookie/РАЗБОР.md`
 * (раздел 3), сроки жизни от 90 до 400 дней.
 *
 * ⚠ Чего здесь НЕТ и почему:
 *  - `w2a_consent` — хранит само решение и обязана пережить очистку;
 *  - `qb_variant_id` и СЕРВЕРНАЯ кука `qb_visitor_id` (липкость смарт-ссылки,
 *    WEB-253/WEB-1257) — это выбор варианта, а не атрибуция; их удаление
 *    перебросило бы посетителя в другую ветку A/B-теста;
 *  - идентификатор посещения `sessionId` — на нём держатся счётчики воронки
 *    (прямой запрет WEB-2145 §4 п.1).
 */
export const ATTRIBUTION_COOKIES: readonly string[] = [
  '_fbc',
  '_fbp',
  // WEB-2759 — куку пикселя TikTok плеер читает и шлёт на сервер под
  // согласием (`clickIds.ttp`), поэтому отказ снимает её наравне с `_fbp`.
  '_ttp',
  '_aff_click_id',
  '_aff_click_param',
  // WEB-2352 — рекламный клик последнего касания (Google/Meta/TikTok) и имя
  // его параметра. Пара пишется под «Принять» тем же `captureClickIds()`, что
  // партнёрская, — значит и сниматься отказом обязана вместе с ней, иначе
  // кнопка «Отклонить» оставляла бы рекламный след на 90 дней.
  '_ad_click_id',
  '_ad_click_param',
  '_ft_click_id',
  'qb_utm_lt',
  'qb_utm_ft',
];

/**
 * WEB-2145 п.2 — атрибуционные записи в хранилищах страницы:
 *  - `qb_visitor_id` (localStorage) — постоянный идентификатор посетителя;
 *  - `qb_utm_params` (sessionStorage) — легаси-метки до WEB-1436. Без него
 *    очистка отменяла бы саму себя: `getStoredUtm()` мигрирует легаси-ключ
 *    обратно в куку при первом же чтении.
 */
export const ATTRIBUTION_STORAGE_KEYS: readonly string[] = [
  'qb_visitor_id',
  UTM_STORAGE_KEY,
];

/**
 * WEB-2140 — составные публичные суффиксы, на которых наивное правило «домен
 * до предпоследней точки» даёт домен ВЕРХНЕГО уровня: браузер такую куку
 * отвергает молча, и согласие не сохраняется вовсе.
 *
 * ⚠ Граница названа прямо: это ПОДБОРКА самых частых зон, а не полный список
 * публичных суффиксов (полный PSL — отдельная зависимость с собственным
 * сроком годности). Промах подборки не имеет права уронить запись решения,
 * поэтому `writeConsentDecision` проверяет запись ФАКТОМ ЧТЕНИЯ и при промахе
 * откатывается на точный хост (см. там же).
 */
const MULTI_LABEL_PUBLIC_SUFFIXES: ReadonlySet<string> = new Set<string>([
  'co.uk', 'org.uk', 'me.uk', 'gov.uk', 'ac.uk', 'net.uk', 'ltd.uk', 'plc.uk',
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au', 'id.au',
  'com.br', 'net.br', 'org.br', 'gov.br',
  'com.mx', 'com.ar', 'com.co', 'com.pe', 'com.uy', 'com.ec', 'com.ve',
  'co.jp', 'ne.jp', 'or.jp', 'ac.jp', 'go.jp',
  'co.kr', 'or.kr', 'ne.kr',
  'co.nz', 'net.nz', 'org.nz', 'govt.nz',
  'co.za', 'org.za', 'net.za',
  'co.in', 'net.in', 'org.in', 'gen.in', 'firm.in',
  'com.sg', 'com.hk', 'com.tw', 'com.tr', 'com.cn', 'com.my', 'com.ph',
  'com.pl', 'com.ua', 'com.pk', 'com.ng', 'com.eg', 'com.sa',
  'co.il', 'co.id', 'co.th', 'com.vn',
]);

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

/** Нормализация хоста: нижний регистр + без завершающей точки (FQDN-форма). */
function normalizeHost(host: string): string {
  return host.toLowerCase().replace(/\.$/, '');
}

function currentHost(): string {
  try {
    return typeof window === 'undefined' ? '' : window.location.hostname;
  } catch {
    return '';
  }
}

function isIpLiteral(host: string): boolean {
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return true;
  return host.includes(':');
}

/**
 * Whether a consent prompt must be shown for this visitor. Fail-closed: an
 * unknown country (null/undefined — VPN/absent/CF-bypassed) requires consent.
 */
export function requiresEuConsent(country: string | null | undefined): boolean {
  if (!country) return true;
  return EU_CONSENT_COUNTRIES.has(country.toUpperCase());
}

/**
 * WEB-2140 — на какой домен ставится кука согласия, чтобы она была видна и на
 * поддомене клиента, и на его корневом домене. `undefined` — только точный
 * хост (прежнее поведение).
 *
 * `undefined` возвращается в четырёх случаях, и каждый — про то, чтобы
 * согласие НЕ уехало не туда:
 *  1. НАШ общий апекс воронок и любой его поддомен: все клиенты живут на его
 *     поддоменах, и кука на родителе отдала бы решение, данное на воронке
 *     одного клиента, воронке другого;
 *  2. `localhost` и адрес числом — родителя у них нет по построению;
 *  3. сам публичный суффикс (`co.uk`, `com`) — родителя нет;
 *  4. хост короче, чем требует его зона.
 */
export function resolveConsentCookieDomain(
  host: string,
  apex: string,
): string | undefined {
  const normalized = normalizeHost(host);
  if (!normalized) return undefined;
  // Наш стенд/апекс и его поддомены — тот же предикат, что решает про
  // служебный параметр показа баннера (config/publicEntryHosts.ts).
  if (isStagingConsentHost(normalized, apex)) return undefined;
  return registrableParentDomain(normalized);
}

/**
 * Регистрируемый домен хоста с ведущей точкой (`.client.com`) — общий счёт
 * меток для куки согласия и для очистки атрибуционных кук. `undefined`, если
 * родителя нет по построению: адрес числом, `localhost`, сам публичный суффикс
 * или хост короче, чем требует его зона.
 *
 * Подборка составных суффиксов (`MULTI_LABEL_PUBLIC_SUFFIXES`) неполна по
 * построению — это НЕ полный публичный список, а частые случаи. Промах в
 * сторону более короткого родителя ведёт к записи, которую браузер молча
 * отвергнет (у куки согласия на это есть откат по факту чтения), а у удаления
 * промах безвреден: лишнее удаление ничего не ломает.
 */
function registrableParentDomain(normalizedHost: string): string | undefined {
  if (!normalizedHost) return undefined;
  if (isIpLiteral(normalizedHost)) return undefined;

  const labels = normalizedHost.split('.').filter(Boolean);
  if (labels.length < 2) return undefined;

  const lastTwo = labels.slice(-2).join('.');
  const lastThree = labels.length >= 3 ? labels.slice(-3).join('.') : undefined;
  let suffixLabels = 1;
  if (MULTI_LABEL_PUBLIC_SUFFIXES.has(lastTwo)) suffixLabels = 2;
  if (lastThree && MULTI_LABEL_PUBLIC_SUFFIXES.has(lastThree)) suffixLabels = 3;

  const registrableLabels = suffixLabels + 1;
  if (labels.length < registrableLabels) return undefined;
  return `.${labels.slice(-registrableLabels).join('.')}`;
}

/**
 * Фикс-пачка 45 (C2) — на какой домен идёт УДАЛЕНИЕ атрибуционных кук.
 *
 * Отличие от `resolveConsentCookieDomain` ровно одно: НАШ апекс здесь НЕ
 * исключается. Исключение там защищает от того, чтобы решение, данное на
 * воронке одного клиента, распространилось на воронку другого — то есть
 * бережёт ЗАПИСЬ согласия. Удаление чужого следа такой опасности не несёт:
 * `_fbp`/`_fbc` библиотека Meta ставит на регистрируемый домен
 * (`.<апекс>`, на бою `.subster.ai`), и без этого расчёта «Отклонить» на
 * `<slug>.<апекс>` не стирал их вовсе. Побочный эффект — на нашем
 * апексе отказ одной воронки снимает куку Meta и для соседних; это верно по
 * сути: кука там ОДНА на всех, и человек отказался именно от неё.
 */
export function resolveAttributionClearDomain(
  host: string,
): string | undefined {
  return registrableParentDomain(normalizeHost(host));
}

function deleteCookie(name: string, domain?: string): void {
  try {
    const domainPart = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; path=/; max-age=0${domainPart}`;
  } catch {
    /* хранилище недоступно — удаление best-effort */
  }
}

export interface ConsentWriteOptions {
  /**
   * WEB-2140: единое согласие на соседних поддоменах клиента. По умолчанию
   * ВЫКЛЮЧЕНО — решение владельца `2122-К7` от 16.09: расширять правовой
   * периметр на весь домен клиента мы не вправе без его явного включения.
   */
  readonly shareAcrossSubdomains?: boolean;
  /** Хост (для тестов и для вызова из места, где адрес уже известен). */
  readonly host?: string;
  /** Апекс платформы (для тестов). */
  readonly apex?: string;
}

/**
 * Persist the consent decision in the cookie read by the pixel layer.
 * 1-year lifetime, Lax (first-party player navigation only).
 *
 * 🔴 ПОРЯДОК ДЕЙСТВИЙ — требование архитектурного разбора WEB-2140 от 16.09.
 * `readConsentDecision()` берёт ПЕРВОЕ совпадение в `document.cookie`, а при
 * равном пути порядок определяется временем создания: атрибут домена в
 * сортировке не участвует. Значит прежняя запись перекрыла бы новую, и
 * человек, нажавший «Отклонить», продолжал бы считаться согласившимся.
 * Поэтому стираются ОБЕ прежние записи (хостовая и доменная), и только потом
 * пишется новая. Стирание обязано повторять атрибуты записи — хостовая
 * удаляется БЕЗ `domain`, доменная — с ним, иначе не удалит.
 */
export function writeConsentDecision(
  value: ConsentDecision,
  options: ConsentWriteOptions = {},
): void {
  const host = options.host ?? currentHost();
  const apex = options.apex ?? PUBLIC_APEX;
  const domain = resolveConsentCookieDomain(host, apex);

  // 1. Стираем обе прежние записи — новая обязана быть единственной.
  deleteCookie(CONSENT_COOKIE);
  if (domain) deleteCookie(CONSENT_COOKIE, domain);

  const base = `${CONSENT_COOKIE}=${value}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax`;
  const wantsDomain = Boolean(options.shareAcrossSubdomains && domain);

  try {
    document.cookie = wantsDomain ? `${base}; domain=${domain}` : base;
  } catch {
    return;
  }

  // 2. Доменная кука могла быть отвергнута браузером молча (промах подборки
  //    публичных суффиксов). Проверяем ФАКТОМ ЧТЕНИЯ и откатываемся на точный
  //    хост: решение человека дороже единого согласия на поддоменах.
  if (wantsDomain && readConsentDecision() !== value) {
    try {
      document.cookie = base;
    } catch {
      /* записать не удалось — сообщать некому, решение не сохранено */
    }
  }
}

export interface ConsentHostOptions {
  /** Хост посетителя (для тестов и для вызова оттуда, где адрес уже известен). */
  readonly host?: string;
  /**
   * Апекс платформы. У ОЧИСТКИ НЕ ИСПОЛЬЗУЕТСЯ — и это не забытая ветка, а
   * разница, из-за которой отказ однажды не сработал.
   *
   * Домен, с которого стираются атрибуционные куки, считается СВОИМ правилом
   * (`resolveAttributionClearDomain`): их ставит чужая библиотека на
   * регистрируемом родителе хоста, и наш апекс из расчёта не исключается
   * (фикс-пачка 45, C2). Прежний расчёт шёл от домена куки СОГЛАСИЯ
   * (`resolveConsentCookieDomain(host, apex)`), тот на нашем апексе отдаёт
   * `undefined` — и куки Meta на `.<апекс>` переживали «Отклонить».
   *
   * Поле оставлено намеренно: `applyConsentDecision` передаёт ОДИН объект
   * настроек обеим половинам решения, и у второй половины —
   * `writeConsentDecision` — апекс читается по-настоящему (им считается домен
   * куки согласия, `ConsentWriteOptions.apex`). Уберём поле отсюда — и вызовы
   * очистки, которые пишут настройки литералом (так их зовут тесты домена
   * очистки), станут отказом типизации на ровном месте, а читатель решит, что
   * апекс на очистку влияет, раз его сюда передают. Проход по швам пачки 45,
   * п.5.
   */
  readonly apex?: string;
}

/**
 * WEB-2145 п.2 — снять атрибуционный след с устройства посетителя при явном
 * отказе. Куки стираются и на точном хосте, и на родительском домене: после
 * WEB-2140 часть из них могла быть записана на любом из уровней, а удаление в
 * браузере работает только при совпадении домена и пути.
 */
export function clearAttributionStorage(options: ConsentHostOptions = {}): void {
  const host = options.host ?? currentHost();
  // Фикс-пачка 45 (C2): домен удаления считается СВОИМ правилом — см.
  // `resolveAttributionClearDomain`. Прежний расчёт (домен куки согласия) на
  // нашем апексе отдавал `undefined`, и куки Meta, стоящие на
  // `.<апекс>`, переживали отказ.
  const domain = resolveAttributionClearDomain(host);

  for (const name of ATTRIBUTION_COOKIES) {
    deleteCookie(name);
    if (domain) deleteCookie(name, domain);
  }

  // Ревизия 45 (C10 + C1): идентификатор посетителя живёт не только в
  // хранилище, но и в памяти модуля. Не сбросив её, очистка отменяла бы саму
  // себя — следующее чтение вернуло бы стёртое значение и записало его
  // обратно. Сброс идёт ДО удаления ключей: то же движение перекладывает
  // значение в ключ отзыва, иначе отказ нечем довезти до сервера.
  forgetVisitorIdForRevocation();

  for (const key of ATTRIBUTION_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* приватный режим — чистить нечего */
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* приватный режим — чистить нечего */
    }
  }

  // Память страницы держит последний набор меток и записала бы его обратно в
  // куку при первом же чтении — очистка отменила бы саму себя.
  resetUtmCache();
}

/**
 * WEB-2162 п.4 — сигнал браузера Global Privacy Control («не продавать и не
 * передавать мои данные»). Посетитель включает его у себя, и закон штата
 * Вашингтон требует его учесть, а не игнорировать.
 */
export function isGlobalPrivacyControlEnabled(): boolean {
  try {
    if (typeof navigator === 'undefined') return false;
    return (
      (navigator as unknown as Record<string, unknown>).globalPrivacyControl ===
      true
    );
  } catch {
    return false;
  }
}

/**
 * WEB-2162 п.4 — решение о ПЕРЕДАЧЕ данных третьим лицам (пиксели, серверные
 * конверсии, постбэки партнёрам).
 *
 * Развилка карточки закрыта по её же рекомендации: сигнал трактуется как
 * отказ ТОЛЬКО от передачи — это его буквальное значение, расширять его на
 * собственный сбор мы не обязаны. Поэтому наша аналитика (событие воронки,
 * счётчики экранов) продолжает работать, а наружу не уходит ничего.
 *
 * Явное «Принять» на баннере сигнал НЕ перекрывает: человек, включивший его в
 * браузере, выразил общее возражение, и частное согласие на нашей странице не
 * должно его отменять.
 */
export function resolveSharingConsent(
  decision: ConsentDecision | undefined,
): ConsentDecision | undefined {
  if (isGlobalPrivacyControlEnabled()) return 'denied';
  return decision;
}

/**
 * WEB-2145 п.1-2 + WEB-2146 п.2 — одно действие на ВСЕ последствия решения
 * посетителя, чтобы обе публичные страницы не расходились:
 *  - «Принять» → решение записано, метки из АДРЕСНОЙ СТРОКИ дозаписаны сразу
 *    (адрес переживает весь визит: переход квиз→пейволл идёт подменой адреса,
 *    исходные метки остаются до самого ухода на оплату — значит у
 *    согласившихся не теряется ничего);
 *  - «Отклонить» → решение записано, атрибуционный след снят.
 *
 * ⚠ Отзыв согласия у уже загруженного пикселя здесь НЕ делается: это
 * `revokeTrackingConsent()` из `pixelLoader.ts`, и звать его отсюда нельзя —
 * pixelLoader → playerPixelEvents → euConsent, вышел бы круг импортов.
 * Публичные страницы зовут обе функции подряд (WEB-2147 п.2).
 */
export function applyConsentDecision(
  decision: ConsentDecision,
  options: ConsentWriteOptions = {},
): void {
  writeConsentDecision(decision, options);
  if (decision === 'granted') {
    captureUtmFromUrl();
    captureClickIds();
    // WEB-2146 (остаток отказа 21.09): постоянный идентификатор посетителя —
    // такая же запись на устройство, как рекламные куки, и политика
    // (`i18n/dictionaries/legal.ts`) обещает её ровно здесь — «после нажатия
    // Принять». До решения он жил только в памяти страницы (`visitorId.ts`),
    // теперь оседает на устройстве ТЕМ ЖЕ значением, каким уже пользовались
    // резолв варианта и связка провайдера в этой загрузке.
    //
    // ⚠ Порядок строк важен: кука решения записана ВЫШЕ, и без неё гейт
    // внутри `getVisitorId()` отказал бы записывать — согласившийся остался
    // бы без идентификатора до следующей страницы.
    getVisitorId();
    return;
  }
  clearAttributionStorage(options);
}
